import React, { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Box,
  Typography,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material'
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CalendarMonth as CalendarMonthIcon,
  Palette as PaletteIcon,
  Category as CategoryIcon,
} from '@mui/icons-material'
import { interventionConfigsAPI, tracksAPI } from '../../services/api'
import { useTrackTypes, getTypeDefaults } from '../../hooks/useTrackTypes'
import PathwayIcon from '../presentation/PathwayIcon'
import ScheduleConfigDialog from './ScheduleConfigDialog'
import PresentationOverrideDialog from './PresentationOverrideDialog'
import ResourceCategoriesDialog from './ResourceCategoriesDialog'

/**
 * Only the scheduling columns of an association. POST /tracks upserts and
 * applies server defaults to any scheduling field it does not receive, so
 * every edit re-sends these. It deliberately omits `custom_settings` and
 * `presentation`: those are only replaced when sent, and re-sending stale
 * copies would clobber edits made elsewhere.
 */
function schedulingPayload(pathway) {
  return {
    track_id: pathway.id,
    position: pathway.position,
    cadence: pathway.cadence,
    is_mandatory: pathway.is_mandatory,
    times_per_week: pathway.times_per_week,
    times_per_period: pathway.times_per_period,
    period_days: pathway.period_days,
    min_spacing_days: pathway.min_spacing_days,
    base_priority: pathway.base_priority,
  }
}

/**
 * Build a human-readable schedule summary for a pathway
 */
function getScheduleSummary(pathway, agendaEligible) {
  if (!agendaEligible) {
    return 'On-demand (patient-initiated, never scheduled)'
  }
  const cadence = pathway.cadence || 'weekly'
  const mandatory = pathway.is_mandatory ? ', required' : ''

  switch (cadence) {
    case 'daily':
      return `Daily${mandatory}`
    case 'weekly': {
      const n = pathway.times_per_week ?? 3
      return `Weekly ${n}x${mandatory}`
    }
    case 'periodic': {
      const n = pathway.times_per_period ?? 2
      const d = pathway.period_days ?? 30
      return `${n}x / ${d}d${mandatory}`
    }
    default:
      return cadence
  }
}

function PathwaysTab({ interventionId, canEdit, onError }) {
  const { typesByCode } = useTrackTypes()
  const [pathways, setPathways] = useState([])
  const [availableTracks, setAvailableTracks] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [addError, setAddError] = useState(null)
  const [selectedTrack, setSelectedTrack] = useState(null)
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false)
  const [schedulePathway, setSchedulePathway] = useState(null)
  const [presentationPathway, setPresentationPathway] = useState(null)
  const [categoriesPathway, setCategoriesPathway] = useState(null)

  const fetchPathways = useCallback(async () => {
    try {
      setLoading(true)
      const data = await interventionConfigsAPI.getTracks(interventionId)
      setPathways(data)
    } catch (err) {
      console.error('Error fetching pathways:', err)
      onError?.('Failed to load pathways')
    } finally {
      setLoading(false)
    }
  }, [interventionId, onError])

  // All library tracks (not just active): attached pathways may be inactive
  // and the rows still need their library presentation for placeholders.
  const fetchAvailableTracks = useCallback(async () => {
    try {
      const response = await tracksAPI.getAll()
      const data = response.data || response
      setAvailableTracks(data)
    } catch (err) {
      console.error('Error fetching tracks:', err)
    }
  }, [])

  useEffect(() => {
    fetchPathways()
    fetchAvailableTracks()
  }, [fetchPathways, fetchAvailableTracks])

  const libraryById = useMemo(
    () => Object.fromEntries(availableTracks.map((track) => [track.id, track])),
    [availableTracks],
  )

  // Registry flags; an unknown type (registry not loaded yet) is treated
  // as a regular agenda pathway, matching the pre-registry behaviour.
  const typeOf = (trackType) => typesByCode[trackType]
  const isAgendaEligible = (trackType) => typeOf(trackType)?.agendaEligible !== false
  const isSingleton = (trackType) => typeOf(trackType)?.singletonPerIntervention === true
  const typeLabel = (trackType) => typeOf(trackType)?.label || trackType
  // Registry-declared intervention extension (resource_finder today); the
  // type code is the fallback until the registry has loaded.
  const hasResourceCategories = (trackType) => {
    const keys = typeOf(trackType)?.presentation?.extensions?.intervention
    return Array.isArray(keys) ? keys.includes('resource_categories') : trackType === 'resource_finder'
  }
  // The content groups a resource_finder pathway searches live on the
  // library track's type_config; the categories dialog scopes its theme
  // counts to them.
  const contentGroupIds = (pathway) => {
    const ids = libraryById[pathway.id]?.type_config?.content_group_ids
    return Array.isArray(ids) ? ids : []
  }

  /** Resolved display for a row: override → library → type default. */
  const resolvePresentation = (pathway) => {
    const override = pathway.presentation || {}
    const library = libraryById[pathway.id]?.presentation || {}
    const defaults = getTypeDefaults(typeOf(pathway.track_type))
    return {
      display_name: override.display_name || library.display_name || pathway.name,
      icon: override.icon || library.icon || defaults.icon,
      overridden: Boolean(override.display_name || override.description || override.icon),
    }
  }

  const handleAddPathway = async () => {
    if (!selectedTrack) return

    try {
      setSaving(true)
      setAddError(null)
      await interventionConfigsAPI.addTrack(interventionId, {
        track_id: selectedTrack.id,
        base_priority: 50,
      })
      await fetchPathways()
      setDialogOpen(false)
      setSelectedTrack(null)
    } catch (err) {
      console.error('Error adding pathway:', err)
      if (err.response?.status === 409 && err.response.data?.code === 'singleton_type_exists') {
        const label = typeLabel(err.response.data.track_type || selectedTrack.track_type)
        setAddError(
          `This intervention already has a ${label} pathway. Only one pathway of that type `
          + 'can be attached — remove the existing one first.',
        )
        // Refresh so the dialog hides the type if the list was stale.
        fetchPathways()
      } else {
        setAddError(err.response?.data?.error || 'Failed to add pathway')
      }
    } finally {
      setSaving(false)
    }
  }

  const handleRemovePathway = async (trackId) => {
    try {
      setSaving(true)
      await interventionConfigsAPI.removeTrack(interventionId, trackId)
      setPathways(pathways.filter(p => p.id !== trackId))
    } catch (err) {
      console.error('Error removing pathway:', err)
      onError?.('Failed to remove pathway')
    } finally {
      setSaving(false)
    }
  }

  const handlePriorityChange = async (pathway, newPriority) => {
    const priority = Math.max(0, Math.min(100, parseInt(newPriority) ?? 50))
    if (priority === pathway.base_priority) return

    try {
      setSaving(true)
      await interventionConfigsAPI.updateTrack(interventionId, {
        ...schedulingPayload(pathway),
        base_priority: priority,
      })
      await fetchPathways()
    } catch (err) {
      console.error('Error updating priority:', err)
      onError?.('Failed to update priority')
    } finally {
      setSaving(false)
    }
  }

  const handleOpenSchedule = (pathway) => {
    setSchedulePathway(pathway)
    setScheduleDialogOpen(true)
  }

  const handleSaveSchedule = async (scheduleData) => {
    try {
      setSaving(true)
      await interventionConfigsAPI.updateTrack(interventionId, scheduleData)
      await fetchPathways()
      setScheduleDialogOpen(false)
      setSchedulePathway(null)
    } catch (err) {
      console.error('Error saving schedule:', err)
      onError?.('Failed to save schedule configuration')
    } finally {
      setSaving(false)
    }
  }

  // Rejections propagate to the dialog, which shows validation inline.
  const handleSavePresentation = async (presentation) => {
    try {
      setSaving(true)
      await interventionConfigsAPI.updateTrack(interventionId, {
        ...schedulingPayload(presentationPathway),
        presentation,
      })
      await fetchPathways()
      setPresentationPathway(null)
    } finally {
      setSaving(false)
    }
  }

  // Same upsert as the override dialog: scheduling fields re-sent, the full
  // presentation object (other keys preserved by the dialog), never
  // custom_settings. Rejections propagate to the dialog.
  const handleSaveResourceCategories = async (presentation) => {
    try {
      setSaving(true)
      await interventionConfigsAPI.updateTrack(interventionId, {
        ...schedulingPayload(categoriesPathway),
        presentation,
      })
      await fetchPathways()
      setCategoriesPathway(null)
    } finally {
      setSaving(false)
    }
  }

  // Active library tracks not yet attached, minus singleton types this
  // intervention already has one of.
  const attachedSingletonTypes = new Set(
    pathways.map((p) => p.track_type).filter((t) => isSingleton(t)),
  )
  const unattachedTracks = availableTracks.filter(
    (track) =>
      track.is_active !== false
      && !pathways.some((p) => p.id === track.id)
      && !attachedSingletonTypes.has(track.track_type),
  )
  const hiddenSingletonLabels = [...attachedSingletonTypes].map(typeLabel)

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={3}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography variant="body2" color="text.secondary">
          {pathways.length} pathway{pathways.length !== 1 ? 's' : ''} attached
        </Typography>
        {canEdit && (
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => {
              setAddError(null)
              setDialogOpen(true)
            }}
            disabled={unattachedTracks.length === 0}
          >
            Add Pathway
          </Button>
        )}
      </Box>

      {pathways.length === 0 ? (
        <Alert severity="info">
          No pathways attached yet. Add pathways to define the content and activities for this intervention.
        </Alert>
      ) : (
        <Paper variant="outlined">
          <List disablePadding>
            {pathways.map((pathway, index) => {
              const agendaEligible = isAgendaEligible(pathway.track_type)
              const display = resolvePresentation(pathway)
              const categoryCount = Array.isArray(pathway.presentation?.resource_categories)
                ? pathway.presentation.resource_categories.length
                : 0
              return (
                <ListItem
                  key={pathway.id}
                  divider={index < pathways.length - 1}
                  sx={{ py: 1, px: 2 }}
                >
                  <PathwayIcon name={display.icon} size={18} sx={{ mr: 1.5, color: 'text.secondary' }} />
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" fontWeight={500}>
                          {pathway.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {typeLabel(pathway.track_type)}
                        </Typography>
                        {pathway.is_base && (
                          <Typography variant="caption" color="primary.main" fontSize="0.6875rem">
                            Base
                          </Typography>
                        )}
                      </Box>
                    }
                    secondary={
                      <Box component="span" display="flex" gap={2} mt={0.25}>
                        <Typography variant="caption" color="text.secondary" component="span">
                          {getScheduleSummary(pathway, agendaEligible)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" component="span">
                          Pos {pathway.position + 1}
                        </Typography>
                        {pathway.min_spacing_days > 0 && agendaEligible && (
                          <Typography variant="caption" color="text.secondary" component="span">
                            {pathway.min_spacing_days}d spacing
                          </Typography>
                        )}
                        {display.display_name !== pathway.name && (
                          <Typography variant="caption" color="text.secondary" component="span">
                            App: “{display.display_name}”{display.overridden ? ' (override)' : ''}
                          </Typography>
                        )}
                        {hasResourceCategories(pathway.track_type) && (
                          <Typography
                            variant="caption"
                            color={categoryCount ? 'text.secondary' : 'warning.main'}
                            component="span"
                          >
                            {categoryCount
                              ? `${categoryCount} resource categor${categoryCount === 1 ? 'y' : 'ies'}`
                              : 'No resource categories (picker hidden)'}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <Box display="flex" alignItems="center" gap={0.5}>
                    {canEdit && agendaEligible && (
                      <Tooltip title="Base priority (0-100). Set to 0 to keep dormant unless strategy-boosted.">
                        <TextField
                          key={`${pathway.id}-${pathway.base_priority}`}
                          type="number"
                          size="small"
                          defaultValue={pathway.base_priority ?? 50}
                          onBlur={(e) => handlePriorityChange(pathway, e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.target.blur()
                            }
                          }}
                          inputProps={{ min: 0, max: 100, style: { textAlign: 'center' } }}
                          sx={{
                            width: 64,
                            '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                              WebkitAppearance: 'none',
                              margin: 0,
                            },
                            '& input[type=number]': {
                              MozAppearance: 'textfield',
                            },
                          }}
                          disabled={saving}
                        />
                      </Tooltip>
                    )}
                    {canEdit && agendaEligible && (
                      <Tooltip title="Schedule settings">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenSchedule(pathway)}
                          disabled={saving}
                        >
                          <CalendarMonthIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canEdit && (
                      <Tooltip title="App presentation (name, description, icon shown to patients)">
                        <IconButton
                          size="small"
                          onClick={() => setPresentationPathway(pathway)}
                          disabled={saving}
                          color={display.overridden ? 'primary' : 'default'}
                        >
                          <PaletteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canEdit && hasResourceCategories(pathway.track_type) && (
                      <Tooltip title="Resource categories (the app's resources picker)">
                        <IconButton
                          size="small"
                          onClick={() => setCategoriesPathway(pathway)}
                          disabled={saving}
                          color={categoryCount ? 'primary' : 'default'}
                        >
                          <CategoryIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canEdit && (
                      <Tooltip title="Remove pathway">
                        <IconButton
                          size="small"
                          onClick={() => handleRemovePathway(pathway.id)}
                          disabled={saving}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </ListItem>
              )
            })}
          </List>
        </Paper>
      )}

      {/* Add Pathway Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Pathway</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            {addError && (
              <Alert severity="error" onClose={() => setAddError(null)}>
                {addError}
              </Alert>
            )}
            <Autocomplete
              options={unattachedTracks}
              getOptionLabel={(option) => option.name}
              value={selectedTrack}
              onChange={(_, newValue) => setSelectedTrack(newValue)}
              renderInput={(params) => (
                <TextField {...params} label="Select Pathway" placeholder="Search..." />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box>
                    <Typography variant="body2">{option.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {typeLabel(option.track_type)} | {option.domain}
                    </Typography>
                  </Box>
                </li>
              )}
            />
            {hiddenSingletonLabels.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                {hiddenSingletonLabels.join(', ')} pathways are hidden: this intervention already has
                one, and only one of that type can be attached.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAddPathway}
            variant="contained"
            disabled={!selectedTrack || saving}
          >
            {saving ? 'Adding...' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Schedule Config Dialog */}
      <ScheduleConfigDialog
        open={scheduleDialogOpen}
        onClose={() => {
          setScheduleDialogOpen(false)
          setSchedulePathway(null)
        }}
        pathway={schedulePathway}
        onSave={handleSaveSchedule}
        saving={saving}
      />

      {/* App Presentation Override Dialog */}
      <PresentationOverrideDialog
        open={Boolean(presentationPathway)}
        onClose={() => setPresentationPathway(null)}
        pathway={presentationPathway}
        libraryPresentation={
          presentationPathway ? libraryById[presentationPathway.id]?.presentation : null
        }
        typeDefaults={
          presentationPathway ? getTypeDefaults(typeOf(presentationPathway.track_type)) : null
        }
        onSave={handleSavePresentation}
        saving={saving}
      />

      {/* Resource Categories Dialog (resource_finder pathway) */}
      <ResourceCategoriesDialog
        open={Boolean(categoriesPathway)}
        onClose={() => setCategoriesPathway(null)}
        pathway={categoriesPathway}
        groupIds={categoriesPathway ? contentGroupIds(categoriesPathway) : []}
        onSave={handleSaveResourceCategories}
        saving={saving}
      />
    </Box>
  )
}

export default PathwaysTab
